import {
    addToHistoryStack,
    setDeletedId,
    setIsDeleting,
    setIsAutoSaving,
    updateHistoryItemDataId,
    setIsBusy,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {deleteCabinetMesh} from 'components/customer/RoomPlanner/helpers/deleteCabinetMesh';
import type {HistoryActionContext} from './historyActionContext';
import type {
    HistoryStack,
    RoomPlannerProduct,
    UndoActionContext,
} from 'components/customer/RoomPlanner/types';
import type {AppDispatch} from 'store/customer/storeSetup';

export interface DispatchCabinetDeleteToHistoryPayload {
    title: string;
    data: {
        id: number;
        values: RoomPlannerProduct;
        undoAction: (ctx?: UndoActionContext) => void | Promise<void>;
    };
}

export const dispatchCabinetDeleteToHistory = (
    dispatch: AppDispatch,
    payload: DispatchCabinetDeleteToHistoryPayload
): void => {
    dispatch(
        addToHistoryStack({
            action: 'CABINET_DELETE',
            isActive: true,
            timestamp: Date.now(),
            title: payload.title,
            data: payload.data,
        })
    );
};

export const undoCabinetDelete = (
    historyItem: HistoryStack,
    targetIndex: number,
    ctx: HistoryActionContext
): void => {
    if (!historyItem.data?.undoAction) {
        ctx.setPreviousHistoryIndex(targetIndex);
        return;
    }

    const existingCabinet = ctx.cabinetGroupListRef.current.find((cabinet) => {
        const cabinetIdValue = String(
            cabinet.userData?.values?.job_cabinet_id ??
                cabinet.userData?.values?.id ??
                ''
        );
        const cabinetIdNumber = Number(
            String(cabinetIdValue).split('-')[0] || cabinetIdValue
        );
        return cabinetIdNumber === Number(historyItem.data?.id);
    });

    if (existingCabinet) {
        ctx.setPreviousHistoryIndex(targetIndex);
        return;
    }

    // Move active index back immediately so rapid Undo doesn't re-run this entry and add duplicate cabinets
    ctx.setPreviousHistoryIndex(targetIndex);
    const undoAction = historyItem.data.undoAction;
    const oldId = historyItem.data?.id;

    // Mark busy so redo cannot fire before the re-added cabinet is in the scene,
    // mirroring the setIsBusy pattern used in redoCabinetAdd.
    ctx.dispatch(setIsBusy(true));

    void undoAction({
        setRestoredCabinetId: (id: number) => {
            if (oldId !== undefined) {
                const numId = Number(oldId);
                if (!Number.isNaN(numId)) {
                    ctx.historyStack.forEach((item, index) => {
                        if (
                            Number(item.data?.id) === numId &&
                            (item.action === 'CABINET_ADD' ||
                                item.action === 'CABINET_DELETE' ||
                                item.action === 'POSITION_CHANGE' ||
                                item.action === 'CABINET_UPDATE')
                        ) {
                            ctx.dispatch(
                                updateHistoryItemDataId({
                                    index,
                                    newId: id,
                                })
                            );
                        }
                    });
                }
            }
            setTimeout(() => {
                ctx.dispatch(setIsBusy(false));
            }, 200);
        },
    });
};

export const redoCabinetDelete = async (
    historyItem: HistoryStack,
    targetIndex: number,
    ctx: HistoryActionContext
): Promise<void> => {
    // Use data.id as the single source of truth — updateHistoryItemDataId keeps it
    // in sync across all history entries, while newCabinetId can become stale when
    // intermediate redo-ADD operations assign a new server ID to the same cabinet.
    const cabinetIdToDelete = historyItem.data?.id;
    if (cabinetIdToDelete == null || !ctx.cabinetGroupListRef?.current) {
        await ctx.setNextHistoryIndex(targetIndex, ctx.targetIndex);
        return;
    }

    const cabinetToDelete = ctx.cabinetGroupListRef.current.find((cabinet) => {
        const cabinetIdValue = String(
            cabinet.userData?.values?.job_cabinet_id ??
                cabinet.userData?.values?.id ??
                ''
        );
        const cabinetIdNumber = Number(
            String(cabinetIdValue).split('-')[0] || cabinetIdValue
        );
        return cabinetIdNumber === Number(cabinetIdToDelete);
    });

    if (!cabinetToDelete) {
        await ctx.setNextHistoryIndex(targetIndex);
        return;
    }

    const rawUserData = cabinetToDelete?.userData;
    if (!rawUserData?.values) {
        await ctx.setNextHistoryIndex(targetIndex);
        return;
    }

    const product = Number(rawUserData.values.type);

    ctx.dispatch(setIsDeleting(true));
    ctx.dispatch(setIsAutoSaving(true));
    ctx.deselectEditingCabinet(Number(cabinetIdToDelete), product);
    ctx.deleteProduct({cabinetId: Number(cabinetIdToDelete)})
        .then(() => {
            ctx.dispatch(setDeletedId(Number(cabinetIdToDelete)));
            deleteCabinetMesh(cabinetToDelete, false, ctx.cabinetGroupListRef);
            ctx.updateTotalProductCountManual(false, false, 1);
            ctx.invalidateCaches();
        })
        .catch(() => {
            ctx.invalidateCaches();
        })
        .finally(() => {
            ctx.dispatch(setIsDeleting(false));
            ctx.dispatch(setIsAutoSaving(false));
        });

    await ctx.setNextHistoryIndex(targetIndex, ctx.targetIndex);
};
